import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  Spinner,
  Box,
} from '@ouster/webviz-react-components';

import { Tooltip } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RiShieldKeyholeLine } from 'react-icons/ri';

import { useStoreState, useStoreActions } from '../reducers';

const DisclaimerBox = ({ text }: { text: string }) => (
  <Box
    bg="yellow.900"
    border="1px solid"
    borderColor="yellow.800"
    borderRadius="8px"
    color="yellow.100"
    fontSize="0.95rem"
    maxW="100ch"
    px={4}
    py={3}
    lineHeight={1.6}
    data-testid="confirm-dialog-disclaimer"
  >
    <Box display="flex" alignItems="flex-start" gap={4}>
      <Box
        as="span"
        color="var(--ouster-colors-yellow-500)"
        lineHeight={0}
        padding="7px"
        backgroundColor="var(--ouster-colors-yellow-800)"
        borderRadius="50%"
      >
        <RiShieldKeyholeLine size={18} />
      </Box>
      <Box>{text}</Box>
    </Box>
  </Box>
);

type ConfirmDialogOverflowTooltipProps = {
  value: string;
  dataTestId: string;
};

function ConfirmDialogOverflowTooltip({
  value,
  dataTestId,
}: ConfirmDialogOverflowTooltipProps) {
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = textRef.current;
    if (!element) return;

    setIsOverflow(
      element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth
    );
  }, []);

  useEffect(() => {
    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [checkOverflow, value]);

  return (
    <Tooltip
      title={
        <span
          style={{
            fontSize: 14,
            lineHeight: '20px',
          }}
        >
          {value}
        </span>
      }
      arrow
      placement="top-start"
      disableHoverListener={!isOverflow}
      disableFocusListener={!isOverflow}
      disableTouchListener={!isOverflow}
      PopperProps={{
        sx: { zIndex: 20003 },
      }}
    >
      <span
        style={{
          display: 'block',
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box
          as="div"
          ref={textRef}
          display="block"
          width="100%"
          minWidth={0}
          style={{
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.25rem',
            maxHeight: '2.5rem',
            wordBreak: 'break-word',
          }}
          data-testid={dataTestId}
        >
          {value}
        </Box>
      </span>
    </Tooltip>
  );
}

export default function ConfirmDialog() {
  const {
    isOpen,
    isLoading,
    onConfirm,
    title,
    body,
    disclaimer,
    bullets,
    details,
    confirmLabel,
    confirmVariant,
    cancelLabel,
    isDestructive,
  } = useStoreState((state) => state.confirmDialog);

  const { closeConfirmDialog, setIsLoading } = useStoreActions(
    (actions) => actions.confirmDialog
  );

  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
      closeConfirmDialog();
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={closeConfirmDialog}
      portalProps={{ containerRef: undefined }}
      data-testid="confirm-dialog-root"
    >
      <AlertDialogOverlay zIndex={20002}>
        <AlertDialogContent
          width="min(42rem, calc(100vw - 6rem))"
          maxWidth="100%"
          overflow="hidden"
          zIndex={20002}
        >
          <AlertDialogHeader
            fontSize="larger"
            fontWeight="bold"
            paddingBottom="1rem"
            borderBottom="1px solid"
            borderColor="gray.500"
            marginBottom="1rem"
            data-testid="confirm-dialog-header"
          >
            {title ?? 'Alert'}
          </AlertDialogHeader>

          <AlertDialogBody data-testid="confirm-dialog-body" overflow="hidden">
            {!isLoading ? (
              <Box
                display="grid"
                rowGap="1.2rem"
                width="100%"
                minWidth={0}
                textAlign="left"
                overflowWrap="anywhere"
              >
                {body ||
                disclaimer ||
                (details && details.length > 0) ||
                (bullets && bullets.length > 0) ? (
                  <>
                    {body && <Box lineHeight={1.5}>{body}</Box>}

                    {details && details.length > 0 && (
                      <Box
                        display="grid"
                        rowGap="0.75rem"
                        width="100%"
                        minWidth={0}
                      >
                        {details.map((detail) => (
                          <Box
                            key={`${detail.label}-${detail.value}`}
                            display="flex"
                            alignItems="flex-start"
                            columnGap="0.5rem"
                            width="100%"
                            minWidth={0}
                          >
                            <Box as="span" flexShrink={0} fontWeight={500}>
                              {detail.label}:
                            </Box>
                            <Box display="block" flex={1} minWidth={0}>
                              <ConfirmDialogOverflowTooltip
                                value={detail.value}
                                dataTestId={`confirm-dialog-detail-${detail.label
                                  .toLowerCase()
                                  .replace(/\s+/g, '-')}`}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {bullets && bullets.length > 0 && (
                      <Box
                        as="ul"
                        display="grid"
                        rowGap="1rem"
                        width="100%"
                        minWidth={0}
                        listStyleType="none"
                        margin={0}
                        padding={0}
                      >
                        {bullets.map((b) => (
                          <li
                            key={b}
                            style={{
                              lineHeight: '1.6rem',
                              display: 'flex',
                              alignItems: 'center',
                              columnGap: '0.5rem',
                              width: '100%',
                              minWidth: 0,
                            }}
                          >
                            <Box as="span" flexShrink={0} aria-hidden>
                              •
                            </Box>
                            <Tooltip
                              title={
                                <span
                                  style={{
                                    fontSize: 14,
                                    lineHeight: '20px',
                                  }}
                                >
                                  {b}
                                </span>
                              }
                              arrow
                              placement="top-start"
                              PopperProps={{
                                sx: { zIndex: 20003 },
                              }}
                            >
                              <span
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <Box
                                  as="span"
                                  display="block"
                                  width="100%"
                                  maxWidth="100%"
                                  minWidth={0}
                                  overflow="hidden"
                                  whiteSpace="nowrap"
                                  textOverflow="ellipsis"
                                  data-testid="confirm-dialog-bullet-overflow-tooltip"
                                >
                                  {b}
                                </Box>
                              </span>
                            </Tooltip>
                          </li>
                        ))}
                      </Box>
                    )}

                    {disclaimer && <DisclaimerBox text={disclaimer} />}

                    {isDestructive && (
                      <p style={{ lineHeight: '1.6rem', fontWeight: 500 }}>
                        This action cannot be undone.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p>Are you sure you want to continue?</p>
                    {isDestructive && (
                      <p style={{ lineHeight: '1.6rem', fontWeight: 500 }}>
                        This action cannot be undone.
                      </p>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Box display="flex" justifyContent="left" columnGap={8}>
                <Spinner />
                Action in progress
              </Box>
            )}
          </AlertDialogBody>

          <AlertDialogFooter data-testid="confirm-dialog-footer">
            <Button
              variant="ghost"
              colorScheme="gray"
              ref={cancelRef}
              onClick={() => closeConfirmDialog()}
              isDisabled={isLoading}
              data-testid="confirm-dialog-cancel-btn"
            >
              {cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={confirmVariant ?? (isDestructive ? 'outline' : 'solid')}
              colorScheme={isDestructive ? 'red' : 'brand'}
              onClick={handleConfirm}
              ml={3}
              isLoading={isLoading}
              data-testid="confirm-dialog-confirm-btn"
            >
              {confirmLabel ?? 'Confirm'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
